import type { ThemeDefinition } from "../types"
import { pair, sharedTokens } from "./_helpers"

/**
 * Carbon Ledger - Dark premium theme
 * Personality: premium, silent, data-authoritative, structured
 * Use: operations-heavy, analytics-heavy, high-density dashboards
 */
const theme: ThemeDefinition = {
  id: "carbon-ledger",
  label: "Carbon Ledger",
  brand: {
    appName: "ECA Triage Accelerator",
    shortName: "ECA",
  },
  tokens: {
    shared: {
      ...sharedTokens,
      "--theme-radius": "4px",
      "--theme-border-width": "1px",
      "--theme-shadow-opacity": "0.05",
      "--theme-font-sans": "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
      "--theme-font-mono": "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
      "--theme-font-heading": "'Inter', ui-sans-serif, system-ui, sans-serif",
      "--theme-accent-ring": "hsl(var(--primary) / 0.20)",
      "--theme-app-title-color": "hsl(var(--foreground))",
    },
    light: {
      "--background": "220 14% 95%",
      "--foreground": "224 14% 12%",
      "--card": "220 14% 98%",
      "--card-foreground": "224 14% 12%",
      "--popover": "220 14% 98%",
      "--popover-foreground": "224 14% 12%",
      "--primary": "220 60% 50%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "220 14% 92%",
      "--secondary-foreground": "224 14% 12%",
      "--muted": "220 12% 92%",
      "--muted-foreground": "220 8% 42%",
      "--accent": "220 60% 50% / 0.07",
      "--accent-foreground": "220 60% 50%",
      "--destructive": "0 72% 51%",
      "--destructive-foreground": "0 0% 100%",
      "--success": "152 60% 36%",
      "--success-foreground": "0 0% 100%",
      "--warning": "38 92% 50%",
      "--warning-foreground": "0 0% 100%",
      "--info": "199 89% 48%",
      "--info-foreground": "0 0% 100%",
      "--border": "220 14% 86%",
      "--input": "220 14% 86%",
      "--ring": "220 60% 50%",
      "--sidebar-background": "220 14% 93%",
      "--sidebar-foreground": "224 14% 12%",
      "--sidebar-primary": "220 60% 50%",
      "--sidebar-primary-foreground": "0 0% 100%",
      "--sidebar-accent": "220 14% 90%",
      "--sidebar-accent-foreground": "224 14% 12%",
      "--sidebar-border": "220 14% 86%",
      "--sidebar-ring": "220 60% 50%",
      "--theme-shell-accent": "hsl(var(--primary))",
      "--theme-shell-accent-soft": "hsl(var(--primary) / 0.08)",
      "--theme-shell-border": "hsl(var(--border) / 0.85)",
      "--theme-shell-muted": "hsl(var(--muted) / 0.5)",
      "--theme-surface-elevated": "hsl(var(--card))",
      "--theme-surface-subtle": "hsl(var(--muted) / 0.45)",
      "--bg-canvas": "220 14% 95%",
      "--bg-surface": "220 14% 98%",
      "--bg-surface-alt": "220 14% 94%",
      "--bg-raised": "220 14% 98%",
      "--bg-inset": "220 12% 92%",
      "--bg-overlay": "220 14% 98%",
      "--text-primary": "224 14% 12%",
      "--text-secondary": "220 8% 42%",
      "--text-tertiary": "220 8% 56%",
      "--text-inverse": "0 0% 100%",
      "--border-ghost": "220 14% 89%",
      "--selection-bg": "220 60% 50% / 0.15",
      "--selection-text": "224 14% 12%",
      "--scrim": "rgba(0, 0, 0, 0.4)",
    },
    dark: {
      "--background": "224 14% 9%",
      "--foreground": "210 15% 92%",
      "--card": "224 14% 12%",
      "--card-foreground": "210 15% 92%",
      "--popover": "224 14% 14%",
      "--popover-foreground": "210 15% 92%",
      "--primary": "220 60% 58%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "224 12% 16%",
      "--secondary-foreground": "210 15% 92%",
      "--muted": "224 12% 15%",
      "--muted-foreground": "218 10% 58%",
      "--accent": "220 60% 58% / 0.15",
      "--accent-foreground": "220 60% 58%",
      "--destructive": "0 63% 38%",
      "--destructive-foreground": "0 0% 100%",
      "--success": "152 60% 40%",
      "--success-foreground": "0 0% 100%",
      "--warning": "38 88% 50%",
      "--warning-foreground": "0 0% 100%",
      "--info": "199 89% 56%",
      "--info-foreground": "0 0% 100%",
      "--border": "224 12% 19%",
      "--input": "224 12% 19%",
      "--ring": "220 60% 58%",
      "--sidebar-background": "224 14% 8%",
      "--sidebar-foreground": "210 15% 92%",
      "--sidebar-primary": "220 60% 58%",
      "--sidebar-primary-foreground": "0 0% 100%",
      "--sidebar-accent": "224 12% 15%",
      "--sidebar-accent-foreground": "210 15% 92%",
      "--sidebar-border": "224 12% 18%",
      "--sidebar-ring": "220 60% 58%",
      "--theme-shell-accent": "hsl(var(--primary))",
      "--theme-shell-accent-soft": "hsl(var(--primary) / 0.12)",
      "--theme-shell-border": "hsl(var(--border) / 0.75)",
      "--theme-shell-muted": "hsl(var(--muted) / 0.6)",
      "--theme-surface-elevated": "hsl(var(--card) / 0.98)",
      "--theme-surface-subtle": "hsl(var(--muted) / 0.4)",
      "--bg-canvas": "224 14% 9%",
      "--bg-surface": "224 14% 12%",
      "--bg-surface-alt": "224 12% 14%",
      "--bg-raised": "224 14% 15%",
      "--bg-inset": "224 14% 7%",
      "--bg-overlay": "224 14% 14%",
      "--text-primary": "210 15% 92%",
      "--text-secondary": "218 10% 58%",
      "--text-tertiary": "218 8% 42%",
      "--text-inverse": "224 14% 9%",
      "--border-ghost": "224 12% 16%",
      "--selection-bg": "220 60% 58% / 0.2",
      "--selection-text": "210 15% 92%",
      "--scrim": "rgba(0, 0, 0, 0.6)",
    },
  },
  palette: {
    clusters: ["#6366f1", "#a855f7", "#22c55e", "#f59e0b", "#ec4899", "#14b8a6", "#f97316", "#8b5cf6", "#84cc16", "#06b6d4", "#ef4444", "#10b981", "#eab308", "#3b82f6", "#fb923c", "#d946ef", "#4ade80", "#c084fc", "#65a30d", "#0ea5e9"],
    charts: ["hsl(var(--primary))", "#6366f1", "#14b8a6", "#f59e0b", "#a855f7", "#06b6d4", "#ec4899"],
    highlights: ["#EF4444", "#F97316", "#EAB308", "#22C55E", "#06B6D4", "#6366F1", "#A855F7", "#EC4899"],
    annotations: ["#6366F1", "#EF4444", "#F97316", "#EAB308", "#22C55E", "#06B6D4", "#A855F7", "#EC4899"],
    bookmarks: ["#6366F1", "#EF4444", "#22C55E", "#EAB308", "#A855F7", "#06B6D4"],
    speakers: ["#6366F1", "#EF4444", "#22C55E", "#EAB308", "#A855F7", "#06B6D4", "#F97316", "#EC4899"],
    chronology: ["#6366f1", "#a855f7", "#ef4444", "#f59e0b", "#71717a"],
    venn: [pair("rgba(99,102,241,0.22)", "#6366F1"), pair("rgba(249,115,22,0.22)", "#F97316"), pair("rgba(34,197,94,0.22)", "#22C55E"), pair("rgba(168,85,247,0.22)", "#A855F7"), pair("rgba(236,72,153,0.22)", "#EC4899")],
    redaction: { pending: pair("rgba(234,179,8,0.3)", "rgb(234,179,8)"), approved: pair("rgba(34,197,94,0.3)", "rgb(34,197,94)"), rejected: pair("rgba(239,68,68,0.3)", "rgb(239,68,68)"), selection: pair("rgba(99,102,241,0.2)", "rgb(99,102,241)") },
    waveform: { primary: "#6366F1", secondary: "#A5B4FC", glow: "rgba(99,102,241,0.3)", success: "#22C55E", error: "#EF4444" },
    graphEntities: { person: pair("rgba(99,102,241,0.14)", "#6366f1"), organization: pair("rgba(168,85,247,0.14)", "#a855f7"), email: pair("rgba(6,182,212,0.14)", "#06b6d4"), domain: pair("rgba(249,115,22,0.14)", "#f97316"), location: pair("rgba(34,197,94,0.14)", "#22c55e") },
    graphDocument: pair("rgba(99,102,241,0.08)", "#6366f1"),
    graphDefaultEntity: pair("rgba(113,113,122,0.14)", "#71717a"),
    lasso: { stroke: "#6366f1", fill: "rgba(99,102,241,0.08)" },
    emphasis: "#6366f1",
  },
}

export default theme
